import { computed, ref, isRef, isReactive, type Ref, type ComputedRef } from "vue";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

export function useDateRangeLimit(
  limit: Ref<number> | ComputedRef<number> | number = 15, 
  typeParam: Ref<"days" | "months" | undefined> | ComputedRef<"days" | "months"> | "days" | "months" = "days",
  unlimitedParam: Ref<boolean> | ComputedRef<boolean> | boolean = false,
) {
  const rangeLimit = computed(() => (isRef(limit) || isReactive(limit)) ? limit.value : limit); // Default to 15 days if no limit is provided
  const type = computed(() => (isRef(typeParam) || isReactive(typeParam)) ? typeParam.value : typeParam); // Default to "days" if no type is provided
  const unlimited = computed(() => (isRef(unlimitedParam) || isReactive(unlimitedParam)) ? unlimitedParam.value : unlimitedParam); // Default to false if no unlimited provided

  const dates = ref<[Dayjs | null, Dayjs | null]>([null, null]);

  const handleCalendarChange = (val: string[] | [dayjs.Dayjs, dayjs.Dayjs]) => {
    if (!val) {
      dates.value = [null, null];
      return;
    }
    dates.value = [
      val[0] ? dayjs(val[0]).startOf("day") : null,
      val[1] ? dayjs(val[1]).endOf("day") : null
    ];
  };

  const disabledDateRage = (current: Dayjs) => {
    if (!dates.value || dates.value.length === 0) return false;

    if (type.value === "days") {
      const firstDate = dates.value?.[0] ? dayjs(dates.value[0]) : null;
      const endDate = dates.value?.[1] ? dayjs(dates.value[1]) : null;

      const tooLate = firstDate && current.diff(firstDate, "days") >= rangeLimit.value;
      const tooEarly = endDate && endDate.diff(current, "days") >= rangeLimit.value;

      if (firstDate && !endDate) {
        return tooLate;
      } else if (endDate && !firstDate) {
        return tooEarly;
      } else {
        return tooLate || tooEarly;
      }
    } else if (type.value === "months") {
      const start = dates.value?.[0];
      if (!start) return false;
      // Si es plan 5 y no tiene limite de ilimitado por defecto no tiene limite superior
      if (unlimited.value && !rangeLimit?.value) {
        return current.isBefore(start, 'day')
      };
      const endLimit = start.add(rangeLimit.value, 'month');

      // Solo permite seleccionar fechas entre el inicio y el inicio + N meses
      return current.isBefore(start, 'day') || current.isAfter(endLimit, 'day');
    }
  };

  const validateDates = (firstDate: Dayjs, endDate: Dayjs) => {
    if (type.value === "days") {
      const tooLate = firstDate && endDate.diff(firstDate, "days") > rangeLimit.value;
      const tooEarly = endDate && endDate.diff(firstDate, "days") > rangeLimit.value - 1;

      if (firstDate && !endDate) {
        return !tooLate;
      } else if (endDate && !firstDate) {
        return !tooEarly;
      } else {
        return !(tooLate || tooEarly);
      }
    } else if (type.value === "months") {
      console.log("Validating months range", firstDate, endDate, rangeLimit.value);
      if (!firstDate) return false;
      // Si es plan 5 y no tiene limite de ilimitado por defecto no tiene limite superior
      if (unlimited.value && !rangeLimit?.value) {
        return !(endDate.isBefore(firstDate, 'day'))
      };
      const endLimit = firstDate.add(rangeLimit.value, 'month');
      console.log("End limit for months:", endLimit);
      // Solo permite seleccionar fechas entre el inicio y el inicio + N meses
      return !(endDate.isBefore(firstDate, 'day') || endDate.isAfter(endLimit, 'day'));
    }
  };

  return { handleCalendarChange, disabledDateRage, validateDates };
}
